import * as vp from 'vopay';
import { FunctionComponent, JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type InteracBulkPayoutTransactionInfoProps = {
  transaction: vp.InteracBulkPayoutTransactionResponse;
  Notes: string;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<InteracBulkPayoutTransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
  eTransferDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const InteracBulkPayoutTransactionInfo: FC = (props: InteracBulkPayoutTransactionInfoProps) => {
  const { state } = useLocation<DataProps>();
  const { transaction, Notes } = props;
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.InteracBulkPayoutTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('eTransferDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('eTransferDetails')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDetails.transactionDateTime'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('transactionDetails.type'),
            value: t('transactionDetails.interacBulkPayout') || '',
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount || '')}`,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
          },
          {
            display: t('transactionDetails.notes'),
            value: Notes,
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return accountInfoProps;
      },
      eTransferDetails: () => {
        const interacTransferMethodEmail = transaction.EmailAddress !== '';
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('eTransferDetails.name'),
            value: transaction.SenderName,
            visible: false,
          },
          {
            display: t('eTransferDetails.email'),
            value: transaction.EmailAddress,
            visible: interacTransferMethodEmail,
          },
          {
            display: t('eTransferDetails.phone'),
            value: transaction.PhoneNumber,
            visible: !interacTransferMethodEmail,
          },
          {
            display: t('eTransferDetails.recipientName'),
            value: transaction.RecipientName,
            visible: true,
          },
          {
            display: t('eTransferDetails.confirmationNumber'),
            value: transaction.ConfirmationNumber,
            visible: true,
          },
          {
            display: t('eTransferDetails.messageForRecipient'),
            value: transaction.Memo,
            visible: true,
          },
          {
            display: t('eTransferDetails.notes'),
            value: transaction.Notes,
            visible: true,
          },
          {
            display: t('eTransferDetails.clientReferenceNumber'),
            value: transaction.ClientReferenceNumber,
            visible: true,
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('eTransferDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountInfoProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default InteracBulkPayoutTransactionInfo;
